import React, {useState} from 'react'
import {Modal, Button, Form, Table, Badge, Alert} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {GET_CONTACT_SEGMENTS, CREATE_CONTACT_SEGMENT, UPDATE_CONTACT_SEGMENT, DELETE_CONTACT_SEGMENT} from '../../../gql/queries/segments'
import {GET_CONTACT_TAGS} from '../../../gql/queries/tags'
import {ContactSegment, SegmentCondition, SegmentRules, IResGetContactSegments, IResCreateContactSegment, IResUpdateContactSegment, IResDeleteContactSegment} from '../../../types/tags'
import {IResGetContactTags} from '../../../types/tags'
import {toast} from 'react-toastify'
import {useIntl, FormattedMessage} from 'react-intl'
import {
  RECIPIENT_MESSAGES as M,
  BULLET_SEPARATOR,
  EM_DASH,
  REQUIRED_MARK,
} from '../recipientMessages'
import './SegmentManager.css'

interface SegmentManagerProps {
  show: boolean
  onHide: () => void
  onSegmentsUpdated?: () => void
}

export const SegmentManager: React.FC<SegmentManagerProps> = ({
  show,
  onHide,
  onSegmentsUpdated,
}) => {
  const intl = useIntl()
  const [editingSegment, setEditingSegment] = useState<ContactSegment | null>(null)
  const [showBuilder, setShowBuilder] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    matchType: 'all' as 'all' | 'any',
    conditions: [] as SegmentCondition[],
  })

  const {data: segmentsData, loading: loadingSegments, refetch} = useQuery<IResGetContactSegments>(GET_CONTACT_SEGMENTS, {
    skip: !show,
  })

  const {data: tagsData} = useQuery<IResGetContactTags>(GET_CONTACT_TAGS, {
    skip: !show,
  })

  const [createSegment, {loading: creating}] = useMutation<IResCreateContactSegment>(CREATE_CONTACT_SEGMENT)
  const [updateSegment, {loading: updating}] = useMutation<IResUpdateContactSegment>(UPDATE_CONTACT_SEGMENT)
  const [deleteSegment] = useMutation<IResDeleteContactSegment>(DELETE_CONTACT_SEGMENT)

  const segments = segmentsData?.contactSegments || []
  const tags = tagsData?.contactTags || []

  const handleOpenBuilder = (segment?: ContactSegment) => {
    if (segment) {
      setEditingSegment(segment)
      setFormData({
        name: segment.name,
        description: segment.description || '',
        matchType: segment.rules.match_type,
        conditions: segment.rules.conditions || [],
      })
    } else {
      setEditingSegment(null)
      setFormData({
        name: '',
        description: '',
        matchType: 'all',
        conditions: [],
      })
    }
    setShowBuilder(true)
  }

  const handleAddCondition = () => {
    setFormData({
      ...formData,
      conditions: [...formData.conditions, {field: 'tags', operator: 'contains', value: ''}],
    })
  }

  const handleUpdateCondition = (index: number, field: keyof SegmentCondition, value: string) => {
    const newConditions = [...formData.conditions]
    newConditions[index] = {...newConditions[index], [field]: value}
    setFormData({...formData, conditions: newConditions})
  }

  const handleRemoveCondition = (index: number) => {
    setFormData({
      ...formData,
      conditions: formData.conditions.filter((_, i) => i !== index),
    })
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error(intl.formatMessage(M.toastSegmentNameRequired))
      return
    }

    if (formData.conditions.length === 0) {
      toast.error(intl.formatMessage(M.toastSegmentConditionRequired))
      return
    }

    const rules: SegmentRules = {
      match_type: formData.matchType,
      conditions: formData.conditions,
    }

    try {
      if (editingSegment) {
        await updateSegment({
          variables: {
            id: editingSegment.id,
            name: formData.name,
            description: formData.description,
            rules,
          },
        })
        toast.success(intl.formatMessage(M.toastSegmentUpdated))
      } else {
        await createSegment({
          variables: {
            name: formData.name,
            description: formData.description,
            rules,
          },
        })
        toast.success(intl.formatMessage(M.toastSegmentCreated))
      }

      setShowBuilder(false)
      setFormData({name: '', description: '', matchType: 'all', conditions: []})
      refetch()
      onSegmentsUpdated?.()
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(M.toastErrorSaveSegment))
    }
  }

  const handleDelete = async (segmentId: string) => {
    if (!window.confirm(intl.formatMessage(M.confirmDeleteSegment))) {
      return
    }

    try {
      await deleteSegment({variables: {id: segmentId}})
      toast.success(intl.formatMessage(M.toastSegmentDeleted))
      refetch()
      onSegmentsUpdated?.()
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(M.toastErrorDeleteSegment))
    }
  }

  return (
    <>
      <Modal show={show && !showBuilder} onHide={onHide} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title><FormattedMessage {...M.manageSegments} /></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='d-flex justify-content-between align-items-center mb-4'>
            <p className='text-muted mb-0'>
              <FormattedMessage {...M.segmentsHint} />
            </p>
            <Button variant='primary' size='sm' onClick={() => handleOpenBuilder()}>
              <i className='bi bi-plus-lg me-2'></i>
              <FormattedMessage {...M.createSegment} />
            </Button>
          </div>

          {loadingSegments ? (
            <div className='text-center py-4'>
              <div className='spinner-border spinner-border-sm' />
            </div>
          ) : segments.length === 0 ? (
            <Alert variant='info'>
              <FormattedMessage {...M.segmentsEmpty} />
            </Alert>
          ) : (
            <Table hover size='sm'>
              <thead>
                <tr>
                  <th><FormattedMessage {...M.name} /></th>
                  <th><FormattedMessage {...M.description} /></th>
                  <th style={{width: '100px'}}><FormattedMessage {...M.contactsColumn} /></th>
                  <th style={{width: '120px'}}><FormattedMessage {...M.actions} /></th>
                </tr>
              </thead>
              <tbody>
                {segments.map((segment) => (
                  <tr key={segment.id}>
                    <td>
                      <strong>{segment.name}</strong>
                      <div className='small text-muted'>
                        <FormattedMessage
                          {...M.segmentConditions}
                          values={{count: segment.rules.conditions.length}}
                        />
                        {BULLET_SEPARATOR}
                        {intl.formatMessage(
                          segment.rules.match_type === 'all' ? M.matchAll : M.matchAny
                        )}
                      </div>
                    </td>
                    <td className='text-muted small'>{segment.description || EM_DASH}</td>
                    <td>
                      <Badge bg='secondary'>{segment.contactCount || 0}</Badge>
                    </td>
                    <td>
                      <div className='d-flex gap-1'>
                        <Button
                          size='sm'
                          variant='outline-primary'
                          onClick={() => handleOpenBuilder(segment)}
                        >
                          <FormattedMessage {...M.edit} />
                        </Button>
                        <Button
                          size='sm'
                          variant='outline-danger'
                          onClick={() => handleDelete(segment.id)}
                        >
                          <FormattedMessage {...M.delete} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onHide}>
            <FormattedMessage {...M.close} />
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Segment Builder Modal */}
      <Modal show={showBuilder} onHide={() => setShowBuilder(false)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {intl.formatMessage(editingSegment ? M.editSegment : M.newSegment)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>
                <FormattedMessage {...M.segmentName} /> {REQUIRED_MARK}
              </Form.Label>
              <Form.Control
                type='text'
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder={intl.formatMessage(M.segmentNamePlaceholder)}
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label><FormattedMessage {...M.description} /></Form.Label>
              <Form.Control
                type='text'
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder={intl.formatMessage(M.segmentDescPlaceholder)}
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label><FormattedMessage {...M.matchType} /></Form.Label>
              <div>
                <Form.Check
                  inline
                  type='radio'
                  label={intl.formatMessage(M.matchAllOption)}
                  checked={formData.matchType === 'all'}
                  onChange={() => setFormData({...formData, matchType: 'all'})}
                />
                <Form.Check
                  inline
                  type='radio'
                  label={intl.formatMessage(M.matchAnyOption)}
                  checked={formData.matchType === 'any'}
                  onChange={() => setFormData({...formData, matchType: 'any'})}
                />
              </div>
            </Form.Group>

            <div className='mb-3'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <Form.Label className='mb-0'><FormattedMessage {...M.conditions} /></Form.Label>
                <Button variant='outline-primary' size='sm' onClick={handleAddCondition}>
                  <i className='bi bi-plus-lg me-1'></i>
                  <FormattedMessage {...M.addCondition} />
                </Button>
              </div>

              {formData.conditions.length === 0 ? (
                <Alert variant='warning'>
                  <FormattedMessage {...M.noConditions} />
                </Alert>
              ) : (
                <div className='conditions-list'>
                  {formData.conditions.map((condition, index) => (
                    <div key={index} className='condition-item'>
                      <div className='row g-2'>
                        <div className='col-md-3'>
                          <Form.Select
                            size='sm'
                            value={condition.field}
                            onChange={(e) => handleUpdateCondition(index, 'field', e.target.value)}
                          >
                            <option value='tags'>{intl.formatMessage(M.conditionTag)}</option>
                            <option value='email'>{intl.formatMessage(M.email)}</option>
                            <option value='subscription_status'>{intl.formatMessage(M.status)}</option>
                          </Form.Select>
                        </div>
                        <div className='col-md-3'>
                          <Form.Select
                            size='sm'
                            value={condition.operator}
                            onChange={(e) => handleUpdateCondition(index, 'operator', e.target.value)}
                          >
                            <option value='contains'>{intl.formatMessage(M.conditionContains)}</option>
                            <option value='not_contains'>{intl.formatMessage(M.conditionNotContains)}</option>
                            <option value='equals'>{intl.formatMessage(M.conditionEquals)}</option>
                          </Form.Select>
                        </div>
                        <div className='col-md-5'>
                          {condition.field === 'tags' ? (
                            <Form.Select
                              size='sm'
                              value={condition.value}
                              onChange={(e) => handleUpdateCondition(index, 'value', e.target.value)}
                            >
                              <option value=''>{intl.formatMessage(M.selectTag)}</option>
                              {tags.map((tag) => (
                                <option key={tag.id} value={tag.name}>
                                  {tag.name}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              size='sm'
                              type='text'
                              value={condition.value}
                              onChange={(e) => handleUpdateCondition(index, 'value', e.target.value)}
                              placeholder={intl.formatMessage(M.valuePlaceholder)}
                            />
                          )}
                        </div>
                        <div className='col-md-1'>
                          <Button
                            size='sm'
                            variant='outline-danger'
                            onClick={() => handleRemoveCondition(index)}
                          >
                            <i className='bi bi-trash'></i>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowBuilder(false)}>
            <FormattedMessage {...M.cancel} />
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={creating || updating}
          >
            {creating || updating ? (
              <>
                <span className='spinner-border spinner-border-sm me-2' />
                <FormattedMessage {...M.saving} />
              </>
            ) : (
              <>
                {intl.formatMessage(editingSegment ? M.updateSegment : M.createSegment)}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
